/**
 * BarEqualizer - анимация 3 вертикальных баров
 *
 * Всегда отображает 3 бара, которые анимируются при активности
 * - В покое: статичные бары высотой 10/16/8px
 * - При речи: бары пульсируют/анимируются
 */

import { memo } from 'react';
import PropTypes from 'prop-types';
import './BarEqualizer.css';

const BARS_COUNT = 3; // Всегда 3 бара
const STATIC_HEIGHTS = [10, 16, 8]; // Высоты баров в покое (px)
const STATIC_OPACITIES = [0.7, 1, 0.7]; // Прозрачность баров в покое

export const BarEqualizer = memo(({
  isSpeaking = false,
  isUserSpeaking = false,
  userAudioLevel = 0,
  agentAudioLevel = 0,
  primaryColor,
  theme,
  width = 240,
  height = 48,
  isAccentMode = false,
}) => {
  // Определяем активность (либо агент, либо пользователь говорит)
  const isActive = isSpeaking || isUserSpeaking;

  // Используем уровень аудио для масштабирования
  const audioLevel = isUserSpeaking ? userAudioLevel : agentAudioLevel;

  // Цвет баров: в accent mode используем контрастный цвет, иначе primaryColor
  const activeColor = isAccentMode && theme?.accentContrastColor
    ? theme.accentContrastColor
    : (primaryColor || 'var(--widget-primary, #2142FF)');

  return (
    <div
      className="bar-equalizer"
      role="img"
      aria-label={isActive ? "Audio visualization: speaking" : "Audio visualization: idle"}
      aria-live="polite"
      style={{
        width: `${width}px`,
        height: `${height}px`,
      }}
    >
      <div className="bar-equalizer__container">
        {Array.from({ length: BARS_COUNT }).map((_, index) => {
          const staticHeight = STATIC_HEIGHTS[index];
          const staticOpacity = STATIC_OPACITIES[index];

          // Макс. масштаб для анимации (на основе аудио уровня)
          const maxScale = isActive ? 1 + (audioLevel * 4) : 1;

          return (
            <div
              key={index}
              className={`bar-equalizer__bar ${isActive ? 'bar-equalizer__bar--active' : 'bar-equalizer__bar--static'}`}
              style={{
                '--bar-color': activeColor,
                '--max-scale': maxScale,
                '--static-height': `${staticHeight}px`,
                opacity: staticOpacity,
              }}
            />
          );
        })}
      </div>
    </div>
  );
});

BarEqualizer.displayName = 'BarEqualizer';

BarEqualizer.propTypes = {
  isSpeaking: PropTypes.bool,
  isUserSpeaking: PropTypes.bool,
  userAudioLevel: PropTypes.number,
  agentAudioLevel: PropTypes.number,
  primaryColor: PropTypes.string,
  theme: PropTypes.shape({
    variant: PropTypes.oneOf(['light', 'dark']),
    primaryColor: PropTypes.string,
    accentContrastColor: PropTypes.string,
  }),
  width: PropTypes.number,
  height: PropTypes.number,
  isAccentMode: PropTypes.bool,
};
